export const PROTOCOL_VERSION = 2;
export const SNAPSHOT_MESSAGE_TYPE = 1;
export const DEFAULT_TICK_RATE = 20;
export const DEFAULT_SNAPSHOT_RATE = 10;
export const DEFAULT_DEV_TOKEN = "cimara-dev";

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Spawn {
  edge: number;
  s: number;
  lane: number;
}

export interface HelloMessage {
  type: "hello";
  token: string;
  protocolVersion: number;
  clientVersion: string;
  riderId?: number;
}

export interface WelcomeMessage {
  type: "welcome";
  riderId: number;
  worldVersion: string;
  spawn: Spawn;
  tickRate: number;
  snapshotRate: number;
}

export interface InputMessage {
  type: "input";
  seq: number;
  t: number;
  watts: number;
  cadence: number;
  hr?: number;
}

export interface ErrorMessage {
  type: "error";
  code: string;
  message: string;
}

export interface PingMessage {
  type: "ping";
  t: number;
}

export interface PongMessage {
  type: "pong";
  t: number;
}

export type RacePhase = "scheduled" | "registration" | "pen" | "countdown" | "racing" | "finished";

export interface RaceLeader {
  riderId: number;
  name: string;
  position: number;
  gap: number;
  isBot?: boolean;
}

export interface RaceStateMessage {
  type: "race_state";
  raceId: string;
  routeId: number;
  spawn?: { edge: number; s: number; lane: number };
  phase: RacePhase;
  startAt: number;
  lap: number;
  laps: number;
  position: number;
  total: number;
  gap: number;
  leaders: RaceLeader[];
  selfStatus?: "racing" | "finished" | "dnf";
}

export type ControlMessage =
  | HelloMessage
  | WelcomeMessage
  | InputMessage
  | ErrorMessage
  | PingMessage
  | PongMessage
  | RaceStateMessage;

export interface SelfState {
  edgeId: number;
  s: number;
  lane: number;
  speed: number;
  grade: number;
  draft: number;
  flags: number;
}

export interface OtherState {
  riderId: number;
  edgeId: number;
  s: number;
  lane: number;
  speed: number;
  kit: number;
}

export interface Snapshot {
  tick: number;
  self: SelfState;
  near: OtherState[];
  mid: OtherState[];
  far: OtherState[];
}

export const FLAG_DRAFTING = 1;
export const FLAG_FINISHED = 2;
export const FLAG_PAUSED = 4;

export class ProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProtocolError";
  }
}

const HEADER_BYTES = 1 + 4 + 2 + 2 + 2;
const SELF_BYTES = 4 + 4 + 1 + 4 + 4 + 4 + 1;
const OTHER_BYTES = 4 + 4 + 4 + 1 + 4 + 1;

export function encodeSnapshot(snapshot: Snapshot): Uint8Array {
  const counts = [snapshot.near.length, snapshot.mid.length, snapshot.far.length];
  for (const count of counts) {
    if (count > 0xffff) {
      throw new ProtocolError(`snapshot ring too large: ${count}`);
    }
  }

  const size = HEADER_BYTES + SELF_BYTES + OTHER_BYTES * (counts[0] + counts[1] + counts[2]);
  const buffer = new ArrayBuffer(size);
  const view = new DataView(buffer);
  let offset = 0;

  view.setUint8(offset, SNAPSHOT_MESSAGE_TYPE);
  offset += 1;
  view.setUint32(offset, snapshot.tick >>> 0, true);
  offset += 4;
  view.setUint16(offset, counts[0], true);
  offset += 2;
  view.setUint16(offset, counts[1], true);
  offset += 2;
  view.setUint16(offset, counts[2], true);
  offset += 2;

  view.setUint32(offset, snapshot.self.edgeId >>> 0, true);
  offset += 4;
  view.setFloat32(offset, snapshot.self.s, true);
  offset += 4;
  view.setInt8(offset, clampInt(snapshot.self.lane, -128, 127));
  offset += 1;
  view.setFloat32(offset, snapshot.self.speed, true);
  offset += 4;
  view.setFloat32(offset, snapshot.self.grade, true);
  offset += 4;
  view.setFloat32(offset, snapshot.self.draft, true);
  offset += 4;
  view.setUint8(offset, snapshot.self.flags & 0xff);
  offset += 1;

  for (const ring of [snapshot.near, snapshot.mid, snapshot.far]) {
    for (const rider of ring) {
      view.setUint32(offset, rider.riderId >>> 0, true);
      offset += 4;
      view.setUint32(offset, rider.edgeId >>> 0, true);
      offset += 4;
      view.setFloat32(offset, rider.s, true);
      offset += 4;
      view.setInt8(offset, clampInt(rider.lane, -128, 127));
      offset += 1;
      view.setFloat32(offset, rider.speed, true);
      offset += 4;
      view.setUint8(offset, rider.kit & 0xff);
      offset += 1;
    }
  }

  return new Uint8Array(buffer);
}

export function decodeSnapshot(data: ArrayBuffer | Uint8Array): Snapshot {
  const view = data instanceof Uint8Array
    ? new DataView(data.buffer, data.byteOffset, data.byteLength)
    : new DataView(data);

  if (view.byteLength < HEADER_BYTES) {
    throw new ProtocolError("snapshot truncated in header");
  }

  let offset = 0;
  const messageType = view.getUint8(offset);
  offset += 1;
  if (messageType !== SNAPSHOT_MESSAGE_TYPE) {
    throw new ProtocolError(`unexpected snapshot message type ${messageType}`);
  }

  const tick = view.getUint32(offset, true);
  offset += 4;
  const nearCount = view.getUint16(offset, true);
  offset += 2;
  const midCount = view.getUint16(offset, true);
  offset += 2;
  const farCount = view.getUint16(offset, true);
  offset += 2;

  const expected = HEADER_BYTES + SELF_BYTES + OTHER_BYTES * (nearCount + midCount + farCount);
  if (view.byteLength !== expected) {
    throw new ProtocolError(`snapshot length ${view.byteLength} != expected ${expected}`);
  }

  const self: SelfState = {
    edgeId: view.getUint32(offset, true),
    s: view.getFloat32(offset + 4, true),
    lane: view.getInt8(offset + 8),
    speed: view.getFloat32(offset + 9, true),
    grade: view.getFloat32(offset + 13, true),
    draft: view.getFloat32(offset + 17, true),
    flags: view.getUint8(offset + 21),
  };
  offset += SELF_BYTES;

  const readRing = (count: number): OtherState[] => {
    const riders: OtherState[] = [];
    for (let i = 0; i < count; i += 1) {
      riders.push({
        riderId: view.getUint32(offset, true),
        edgeId: view.getUint32(offset + 4, true),
        s: view.getFloat32(offset + 8, true),
        lane: view.getInt8(offset + 12),
        speed: view.getFloat32(offset + 13, true),
        kit: view.getUint8(offset + 17),
      });
      offset += OTHER_BYTES;
    }
    return riders;
  };

  return {
    tick,
    self,
    near: readRing(nearCount),
    mid: readRing(midCount),
    far: readRing(farCount),
  };
}

export function isSnapshotMessage(data: ArrayBuffer | Uint8Array): boolean {
  const bytes = data instanceof Uint8Array
    ? data
    : new Uint8Array(data, 0, Math.min(1, data.byteLength));
  return bytes.byteLength > 0 && bytes[0] === SNAPSHOT_MESSAGE_TYPE;
}

export function parseControlMessage(text: string): ControlMessage {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new ProtocolError("control message is not valid JSON");
  }
  if (!isRecord(value) || typeof value.type !== "string") {
    throw new ProtocolError("control message has no type");
  }

  switch (value.type) {
    case "hello":
      requireString(value, "token");
      requireNumber(value, "protocolVersion");
      requireString(value, "clientVersion");
      optionalNumber(value, "riderId");
      return value as unknown as HelloMessage;
    case "welcome":
      requireNumber(value, "riderId");
      requireString(value, "worldVersion");
      requireNumber(value, "tickRate");
      requireNumber(value, "snapshotRate");
      if (!isRecord(value.spawn)) {
        throw new ProtocolError("welcome.spawn must be an object");
      }
      return value as unknown as WelcomeMessage;
    case "input":
      requireNumber(value, "seq");
      requireNumber(value, "t");
      requireNumber(value, "watts");
      requireNumber(value, "cadence");
      optionalNumber(value, "hr");
      return value as unknown as InputMessage;
    case "race_state":
      requireString(value, "raceId");
      requireNumber(value, "routeId");
      if (value.spawn !== undefined) {
        if (!isRecord(value.spawn)) throw new ProtocolError("race_state.spawn must be an object");
        requireNumber(value.spawn, "edge");
        requireNumber(value.spawn, "s");
        requireNumber(value.spawn, "lane");
      }
      if (typeof value.phase !== "string" || !isRacePhase(value.phase)) {
        throw new ProtocolError("race_state.phase is invalid");
      }
      requireNumber(value, "startAt");
      requireNumber(value, "lap");
      requireNumber(value, "laps");
      requireNumber(value, "position");
      requireNumber(value, "total");
      requireNumber(value, "gap");
      if (!Array.isArray(value.leaders)) {
        throw new ProtocolError("race_state.leaders must be an array");
      }
      for (const leader of value.leaders) {
        if (!isRecord(leader)) throw new ProtocolError("race_state leader must be an object");
        requireNumber(leader, "riderId");
        requireString(leader, "name");
        requireNumber(leader, "position");
        requireNumber(leader, "gap");
      }
      if (value.selfStatus !== undefined && value.selfStatus !== "racing" && value.selfStatus !== "finished" && value.selfStatus !== "dnf") {
        throw new ProtocolError("race_state.selfStatus is invalid");
      }
      return value as unknown as RaceStateMessage;
    case "error":
      requireString(value, "code");
      requireString(value, "message");
      return value as unknown as ErrorMessage;
    case "ping":
    case "pong":
      requireNumber(value, "t");
      return value as unknown as PingMessage | PongMessage;
    default:
      throw new ProtocolError(`unknown control message type ${value.type}`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isRacePhase(value: string): value is RacePhase {
  return (
    value === "scheduled" ||
    value === "registration" ||
    value === "pen" ||
    value === "countdown" ||
    value === "racing" ||
    value === "finished"
  );
}

function requireString(value: Record<string, unknown>, key: string): void {
  if (typeof value[key] !== "string") {
    throw new ProtocolError(`${key} must be a string`);
  }
}

function requireNumber(value: Record<string, unknown>, key: string): void {
  if (typeof value[key] !== "number" || !Number.isFinite(value[key])) {
    throw new ProtocolError(`${key} must be a finite number`);
  }
}

function optionalNumber(value: Record<string, unknown>, key: string): void {
  if (value[key] !== undefined && (typeof value[key] !== "number" || !Number.isFinite(value[key]))) {
    throw new ProtocolError(`${key} must be a finite number when present`);
  }
}

function clampInt(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}
